import React, {useState, useEffect} from 'react'
import moment from 'moment'
import EnhancedTable from '../../../../../../../../../Components/EnhancedTable'
import { headersCommunications } from '../../../../options'
import {
  TableRow,
  TableCell,
  Menu,
  makeStyles,
  Box,
  Typography,
} from '@material-ui/core'
import useGraphQLFetch from '../../../../../../../../../Utils/Hooks/useGraphQLFetch'
import { findEmailStatWithTemplate } from '../../../../gql'
import { EmailIcon, NotificationIcon, SmsIcon } from '../icons'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { InteractionBlocker } from '../../../../../../../../../Components/InteractionBlocker'
import { calculateTimingType, calculateTimingValue } from '../../../../utils'
import RichTextEditor from '../../../../../../../../../Components/RichTextEditor'
import LoadingIndicator from '../../../../../../../../../Components/LoadingIndicator/LoadingIndicator'
import { scheduledTriggers } from '../../../../options'
import { calculateTimeBefore } from '../../../../utils'

const useStyles = makeStyles(() => ({
  dateSequenceTemplateOccurrences: {
    marginLeft: '2.5rem',
  },
  dateSequenceTemplateStat: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  dateSequenceTemplateStatTitle: {
    color: "#798498",
  },
  dateSequenceTemplateStatValue: {
    marginLeft: '0.5rem',
    color: '#00112B',
  },
  dateSequenceTemplateStatsContainer: {
    backgroundColor: '#EAEAFF',
    padding: '0.5rem 1rem',
  },
  previewContainer: { padding: 20, width: '400px' },
  previewHardcodedContainer: { padding: 20, minWidth: '400px' },
  prewviewHeader: { display: 'flex', justifyContent: 'space-between' },
  previewCloseButton: { cursor: 'pointer' },
  previewBody: {
    padding: 10,
    '& .btn-primary': { backgroundImage: 'none !important' },
  },
  previewTitle: {
    marginLeft: '10px',
    marginTop: '10px',
    marginBottom: '10px',
    fontSize: '18px',
    fontWeight: 'bold',
  },
}))

export const SequenceCommsTable = ({
  dateSequenceTemplate,
  communications,
  sequenceId,
  hasHardcodedComms,
  sequenceName,
  trigger,
  cityId,
}) => {
  const {
    dateSequenceTemplateOccurrences,
    dateSequenceTemplateStat,
    dateSequenceTemplateStatTitle,
    dateSequenceTemplateStatValue,
    dateSequenceTemplateStatsContainer,
    previewContainer,
    prewviewHeader,
    previewCloseButton,
    previewTitle,
    previewBody,
    previewHardcodedContainer,
  } = useStyles()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState()
  const [previewAnchorEl, setPreviewAnchorEl] = useState(null)
  const [previewData, setPreviewData] = useState({ isOpen: false })

  let comms = [...communications]

  if (trigger && scheduledTriggers.includes(trigger.triggerType.name)) {
    comms = comms.map(comm => ({
      ...comm,
      time_delay: calculateTimeBefore(trigger.target_value, comm.time_delay),
    }))
  }

  const commsIcons = {
    'EMAIL': <EmailIcon />,
    'NOTIFICATION': <NotificationIcon />,
    'SMS': <SmsIcon />,
  }

  const { data, isLoaded } = useGraphQLFetch({
    fetchFn: findEmailStatWithTemplate,
    defaultFetchArgs: {
      templateIds: communications.reduce((acc, curr) => {
        if (curr.payload.templateId) {
          acc.push({
            templateId: curr.payload.templateId,
            type: curr.actionType.name,
          })
        }
        return acc
      }, []),
      sequenceId,
      cityId,
    },
  })

  const handlePreviewClose = () => {
    setPreviewData({ isOpen: false })
    setPreviewAnchorEl(null)
  }

  useEffect(() => {
    if (
      data &&
      data.findEmailStatWithTemplate &&
      isLoaded &&
      !rows &&
      comms &&
      comms.length
    ) {
      const commsRows = comms.map(communication => {
        const commData = data.findEmailStatWithTemplate.find(
          e =>
            e.templateId == communication.payload.templateId &&
            e.type == communication.actionType.name,
        )
        let openCount = undefined
        let clicksCount = undefined
        let sentCount = undefined

        if (commData) {
          const { openCount: open, clicksCount: clicks, sent } = commData
          openCount = Number(open)
          clicksCount = Number(clicks)
          sentCount = Number(sent)
        }

        const openRate =
          !openCount && !sentCount
            ? '0.00%'
            : String(
                ((openCount / sentCount) * 100).toFixed(2) > 100
                  ? 100
                  : ((openCount / sentCount) * 100).toFixed(2),
              ) + '%'
        const clickRate =
          !clicksCount && !openCount
            ? '0.00%'
            : String(
                ((clicksCount / openCount) * 100).toFixed(2) > 100
                  ? 100
                  : ((clicksCount / openCount) * 100).toFixed(2),
              ) + '%'
        const commRow = {
          params: {
            highLight: null,
            id: communication.payload.templateId,
            disableClick: [],
            timeDelay: Number(communication.time_delay),
            commType: communication.actionType.name || commData.type,
          },
          row: {
            name: commData
              ? hasHardcodedComms
                ? sequenceName
                : commData.name || '-'
              : '-',
            type: communication.actionType.name
              ? commsIcons[communication.actionType.name]
              : '-',
            commDelay: communication.time_delay
              ? Number(communication.time_delay) === 0
                ? '0m'
                : `${Number(calculateTimingValue(communication.time_delay))}${
                    calculateTimingType(communication.time_delay)[0]
                  }`
              : '-',
            sentCount: typeof sentCount === 'number' ? sentCount : '-',
            openCount: typeof openCount === 'number' ? openCount : '-',
            clickCount: typeof clicksCount === 'number' ? clicksCount : '-',
            openRate: openRate,
            clickRate: clickRate,
          },
        }

        return commRow
      })
      commsRows.sort((a, b) => {
        return a.params.timeDelay - b.params.timeDelay
      })
      setRows(commsRows)
      setLoading(false)
    } else if (isLoaded) {
      setLoading(false)
    }
  }, [communications, rows, data, isLoaded])

  const handleRowClick = (e, id, type) => {
    setPreviewAnchorEl(e.currentTarget)
    const templateData = data.findEmailStatWithTemplate.find(
      e => e.templateId == id && e.type == type,
    )

    if (templateData) {
      const commType = templateData.type
      if (hasHardcodedComms) {
        if (commType === 'EMAIL') {
          setPreviewData({
            isOpen: true,
            title: `Subject: ${templateData.subject}`,
            icon: commsIcons['EMAIL'],
            content: (
              <InteractionBlocker>
                <div
                  className={previewBody}
                  dangerouslySetInnerHTML={{ __html: templateData.email }}
                />
              </InteractionBlocker>
            ),
            isHardcodedEmail: true,
          })
        } else if (commType === 'SMS' || commType === 'NOTIFICATION') {
          setPreviewData({
            isOpen: true,
            title:
              commType === 'SMS'
                ? `Name: ${templateData.name || ''}`
                : `Title: ${templateData.title || ''}`,
            icon: commsIcons[commType],
            content: (
              <InteractionBlocker>
                <div className={previewBody}>{templateData.body || ''}</div>
              </InteractionBlocker>
            ),
          })
        }
      } else {
        if (commType === 'EMAIL') {
          setPreviewData({
            isOpen: true,
            title: `Subject: ${templateData.subject}`,
            icon: commsIcons[commType],
            content: (
              <InteractionBlocker>
                <RichTextEditor
                  value={JSON.parse(templateData.email)}
                  readOnly
                  rows="auto"
                  index={`${new Date().getTime()}`}
                />
              </InteractionBlocker>
            ),
          })
        } else if (commType === 'SMS') {
          setPreviewData({
            isOpen: true,
            title: `Name: ${templateData.name || ''}`,
            icon: commsIcons[commType],
            content: (
              <InteractionBlocker>
                <RichTextEditor
                  value={JSON.parse(templateData.body)}
                  readOnly
                  rows="auto"
                  index={`${new Date().getTime()}`}
                />
              </InteractionBlocker>
            ),
          })
        }
        if (commType === 'NOTIFICATION') {
          setPreviewData({
            isOpen: true,
            title: `Title: ${templateData.title || ''}`,
            icon: commsIcons[commType],
            content: (
              <InteractionBlocker>
                <div className={previewBody}>{templateData.body}</div>
              </InteractionBlocker>
            ),
          })
        }
      }
    } else {
      setPreviewData({
        isOpen: true,
        title: `No template found for this action`,
        content: '',
      })
    }
  }

  if (loading) {
    return (
      <TableRow tabIndex={-1}>
        <TableCell style={{ padding: 0 }} colSpan={14}>
          <LoadingIndicator
            size={20}
            labelStyle={{ marginTop: '8px' }}
            style={{ height: '65px' }}
          />
        </TableCell>
      </TableRow>
    )
  }

  const fillerPrefixComponent =  () => (<TableCell
      style={{borderBottom: '0px'}}
  ><Box>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Box> </TableCell>)

  return (
    <TableRow tabIndex={-1}>
      <TableCell style={{ padding: 0 }} colSpan={14}>
        {Boolean(dateSequenceTemplate) && (
          <Box
            className={[
              dateSequenceTemplateStatsContainer,
              dateSequenceTemplateStat,
            ]}
          >
            <Box className={dateSequenceTemplateStat}>
              <Typography className={dateSequenceTemplateStatTitle} variant="body2">Scheduled to be sent:</Typography>
              <Typography
                className={dateSequenceTemplateStatValue}
                variant="subtitle2"
              >
                {moment(dateSequenceTemplate.start_date).format(
                  'DD MMM YYYY, h:mm A',
                )}
              </Typography>
            </Box>
            <Box
              className={[
                dateSequenceTemplateStat,
                dateSequenceTemplateOccurrences,
              ]}
            >
              <Typography className={dateSequenceTemplateStatTitle} variant="body2">Nr of times sent:</Typography>
              <Typography
                className={dateSequenceTemplateStatValue}
                variant="subtitle2"
              >
                {dateSequenceTemplate.ocurrences}
              </Typography>
            </Box>
          </Box>
        )}
        <EnhancedTable
          rowPrefixComponent={fillerPrefixComponent}
          headerPrefixComponent={fillerPrefixComponent}
          headers={headersCommunications}
          rows={rows}
          onClickRow={handleRowClick}
          noDataMessage="There are no communications for this sequence"
          disablePagination
          disableNoDataWrapper
          collapsableTable
        />
        <Menu
          id="long-menu"
          anchorEl={previewAnchorEl}
          keepMounted
          open={previewData.isOpen}
          onClose={handlePreviewClose}
        >
          <div
            className={
              previewData.isHardcodedEmail
                ? previewHardcodedContainer
                : previewContainer
            }
          >
            <div className={prewviewHeader}>
              <div className={previewCloseButton} onClick={handlePreviewClose}>
                <ChevronRightIcon />
              </div>
              <div>{previewData.icon || ''}</div>
            </div>
            <div className={previewTitle} dir="auto">{previewData.title}</div>
            <div dir="auto">{previewData.content}</div>
          </div>
        </Menu>
      </TableCell>
    </TableRow>
  )
}
